import { attrRankController, homePageController } from '~/apis/back/services'
import type { SearchDTO } from '~/apis/back/types'
import { ENUM_EventType, ENUM_PageName, E_CATEGORY_TYPE } from '~/enums'
import type { TabSwitchEvent, ViewItemListEvent } from '~/types/event-tracking'

export function useRank() {
  const { t } = useI18n()
  const route = useRoute()
  const userInfoStore = useUserStore()

  const rankState = useState('rankState', () => ({
    attrRankList: [] as Array<SearchDTO & { listPosition: number }>,
    expRankList: [] as Array<SearchDTO & { listPosition: number }>,
    searchList: [] as Array<SearchDTO & { listPosition: number }>,
    searchCurPage: 1,
    showCitySelect: false,
    attLoading: false,
    expLoading: false,
    finished: false,
    searchLoading: false,
    cityData: {
      cityId: route.params.destinationId as string,
      cityName: '',
      cityNameEn: route.params.cityNameEn as string,
      countryCode: route.params.countryCode as string,
    },
  }))

  const {
    attrRankList,
    expRankList,
    searchList,
    attLoading,
    expLoading,
    searchLoading,
    finished,
    showCitySelect,
    searchCurPage,
    cityData,
  } = toRefs(rankState.value)

  const currentTab = computed(() => {
    return Number(route.query.tabId) === E_CATEGORY_TYPE.EXPERIENCES ? E_CATEGORY_TYPE.EXPERIENCES : E_CATEGORY_TYPE.ATTRACTIONS
  })

  const loading = computed(() => currentTab.value === E_CATEGORY_TYPE.ATTRACTIONS ? attLoading.value : expLoading.value)

  const rankList = computed(() => {
    return currentTab.value === E_CATEGORY_TYPE.ATTRACTIONS ? attrRankList.value : expRankList.value
  })

  function cityChangeHandler(val: { cityId: string, cityName: string, cityNameEn: string, countryCode: string } | null) {
    if (!val) {
      return
    }
    return navigateWithLocale({
      path: getCityUrl(val.countryCode, val.cityNameEn, val.cityId),
      query: {
        ...route.query,
      },
    })
  }

  const rankTypeList = [
    {
      rankType: E_CATEGORY_TYPE.ATTRACTIONS,
      rankTypeName: t('key.rank.tab.top.attracitons'),
    },
    {
      rankType: E_CATEGORY_TYPE.EXPERIENCES,
      rankTypeName: t('key.rank.tab.top.things'),
    },
  ]

  async function queryAttrRank() {
    attLoading.value = true
    const res = await attrRankController.rankListRankAttPost({ destinationId: Number(route.params.destinationId as string) })
    attLoading.value = false
    attrRankList.value = res.data.value?.data?.data?.map((item, index) => ({
      ...item,
      listPosition: index + 1,
    })) ?? []
    trackEvent<ViewItemListEvent>({
      event: ENUM_EventType.ViewItemList,
      item_number: attrRankList.value.length,
      prd_type_id: E_CATEGORY_TYPE.ATTRACTIONS,
      city: cityData.value.cityName,
      city_id: cityData.value.cityId,
      page_category: ENUM_PageName.RankPage,
      loading_type: 'first',
    })
  }

  async function queryExpRank() {
    expLoading.value = true
    const res = await attrRankController.rankListAttSpecialTopicPost({ destinationId: Number(route.params.destinationId as string) })
    expLoading.value = false
    expRankList.value = res.data.value?.data?.data?.map((item, index) => ({
      ...item,
      listPosition: index + 1,
    })) ?? []
    trackEvent<ViewItemListEvent>({
      event: ENUM_EventType.ViewItemList,
      item_number: expRankList.value.length,
      prd_type_id: E_CATEGORY_TYPE.EXPERIENCES,
      city: cityData.value.cityName,
      city_id: cityData.value.cityId,
      page_category: ENUM_PageName.RankPage,
      loading_type: 'first',
    })
  }

  function tabChange(name?: E_CATEGORY_TYPE) {
    if (!name) {
      return
    }
    const route = useRoute()
    const val = route.params as { destinationId: string, cityName: string, countryCode: string }
    return navigateWithLocale({
      path: getCityUrl(val.countryCode, val.cityName, val.destinationId),
      query: {
        ...route.query,
        tabId: name,
      },
    })
  }

  function getCityUrl(countryCode: string, cityName: string, destinationId: string) {
    const formattedCountryCode = formatUrlStr(countryCode)
    const formattedCityName = formatUrlStr(cityName)
    return `/rank/${formattedCountryCode}/${formattedCityName}/${destinationId}`
  }

  function queryData() {
    queryAttrRank()
    queryExpRank()
    searchList.value = []
    searchCurPage.value = 1
    finished.value = false
    querySearchList()
  }

  async function querySearchList() {
    const pageSize = 20
    searchLoading.value = true
    const res = await homePageController.homePageCombineSearchPost({
      pageNum: searchCurPage.value,
      pageSize,
      cityId: cityData.value.cityId,
      keyWord: '',
      currency: userInfoStore.userInfo.currency,
      // categoryId: currentTab.value ? currentTab.value : undefined,
      priceMin: 0,
      priceMax: 1000000000000,
      sortType: ENUM_SORT_TYPE.RECOMMEND,
      cityRankType: currentTab.value === E_CATEGORY_TYPE.ATTRACTIONS ? 1 : 2,
    })
    searchLoading.value = false
    const data = res.data.value?.data?.data?.map((item, index) => ({
      ...item,
      listPosition: (searchCurPage.value - 1) * pageSize + index + 1,
    })) ?? []
    if (searchCurPage.value * pageSize >= (res.data.value?.data?.pageInfo?.total ?? 0)) {
      finished.value = true
    }
    searchList.value.push(...data)
    if (import.meta.client) {
      trackEvent<ViewItemListEvent>({
        event: ENUM_EventType.ViewItemList,
        item_number: searchList.value.length,
        prd_type_id: currentTab.value,
        city: cityData.value.cityName,
        city_id: cityData.value.cityId,
        country_code: cityData.value.countryCode,
        page_category: ENUM_PageName.SearchResultPage,
        loading_type: searchCurPage.value === 1 ? 'first' : 'scroll',
      })
    }
  }

  function scrollQuerySearchList() {
    if (!finished.value && !searchLoading.value && searchList.value.length > 0) {
      searchCurPage.value++
      querySearchList()
    }
  }

  return {
    rankState,
    rankTypeList,
    currentTab,
    cityData,
    showCitySelect,
    loading,
    rankList,
    searchList,
    searchLoading,
    attLoading,
    expLoading,
    finished,
    tabChange,
    queryData,
    queryAttrRank,
    queryExpRank,
    cityChangeHandler,
    scrollQuerySearchList,
  }
}
